import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { ResultCard, StateBox } from "@/components/market/ResultCard";
import { realLocation } from "@/pages/Shop";

const EXAMPLES = ["I need bread near me", "I need a plumber today", "I need 100 chairs for an event", "मुझे नौकरी चाहिए"];


export default function Need() {
  const { t } = useI18n();
  const nav = useNavigate();
  const ctx = useUserLocation() || {};
  const real = realLocation(ctx.loc);
  const saved = real && real.lat != null ? real : null;
  const [text, setText] = useState("");
  const [city, setCity] = useState(real && real.lat == null ? real.city || "" : "");
  const [useLoc, setUseLoc] = useState(!!saved);
  useEffect(() => { if (saved) setUseLoc(true); }, [!!saved]); // eslint-disable-line react-hooks/exhaustive-deps
  const [res, setRes] = useState(null);
  const [state, setState] = useState(null);
  const run = async (q = text) => {
    if (!q.trim()) return;
    setText(q); setState("loading");
    try {
      const params = { text: q, city: city || undefined, ...(useLoc && saved ? { lat: saved.lat, lng: saved.lng } : {}) };
      const { data } = await api.get("/need/discover", { params });
      setRes(data); setState(data.results.length ? "ok" : "empty");
    } catch { setState("error"); }
  };
  const p = res?.parsed;
  return (
    <div className="max-w-4xl mx-auto px-4 pt-6 pb-32 space-y-4" data-testid="need-page">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div><h1 className="text-2xl font-black">{t("market.need_title")}</h1><p className="text-xs text-stone-500">{t("market.need_sub")}</p></div>
        <Link to="/shop" className="text-sm font-bold underline" data-testid="need-to-shop">{t("market.switch_to_shop")}</Link>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="space-y-2">
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder={t("market.need_placeholder")} className="w-full rounded-xl border p-3 text-sm" data-testid="need-text" />
        <div className="flex flex-wrap gap-2 items-center">
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("market.city_optional")} className="h-10 flex-1 min-w-[140px] rounded-xl border px-3 text-sm" data-testid="need-city" />
          <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={useLoc} disabled={!saved} onChange={(e) => setUseLoc(e.target.checked)} data-testid="need-use-location" />{saved ? t("market.use_saved_location") : t("market.no_saved_location")}</label>
          <button className="h-10 px-5 rounded-full bg-stone-900 text-white text-sm font-bold" data-testid="need-submit">{t("market.find")}</button>
        </div>
        <div className="flex flex-wrap gap-2">{EXAMPLES.map((e, i) => <button type="button" key={e} onClick={() => run(e)} className="h-8 px-3 rounded-full border text-xs" data-testid={`need-example-${i}`}>{e}</button>)}</div>
      </form>
      {p && (
        <div className="rounded-2xl bg-stone-50 p-3 text-xs space-y-1" data-testid="need-parsed">
          <div><b>{t("market.understood")}:</b> {p.intent ? t(`market.intent.${p.intent}`) : "—"} {p.entity && `· ${p.entity}`} {p.quantity && `· ${p.quantity} ${p.unit || ""}`}
            {p.time && ` · ${p.time === "within_hours" ? `${p.within_hours}h` : t(`market.time.${p.time}`)}`} {p.budget_max && ` · ≤ ₹${p.budget_max}`} {p.brand && ` · ${p.brand}`}
            {` · ${t("market.confidence")} ${Math.round((p.confidence || 0) * 100)}%`}</div>
          <div className="text-stone-500">{t("market.location_mode")}: {t(`market.loc.${res.location.mode}`)}{res.location.city ? ` (${res.location.city})` : ""}</div>
          {p.clarify && <div className="font-bold text-amber-800" data-testid="need-clarify">{t(`market.clarify.${p.clarify}`)}</div>}
        </div>
      )}
      <StateBox state={state === "ok" ? null : state} onRetry={() => run()} testid="need"
                emptyText={res?.empty_reason === "no_location" ? t("market.empty_no_location") : t("market.empty_no_match")} />
      <div className="grid sm:grid-cols-2 gap-3" data-testid="need-results">{(res?.results || []).map((r, i) => <ResultCard key={`${r.source}${r.id}`} r={r} idx={i} />)}</div>
      {res && (
        <div className="flex flex-wrap gap-2" data-testid="need-actions">
          {res.next_actions.map((a, i) => (
            <button key={i} onClick={() => nav(a.url)} className={`h-10 px-4 rounded-full text-sm font-bold ${a.type === "open" ? "bg-sky-700 text-white" : "border"}`} data-testid={`need-action-${a.type}`}>
              {t(`market.action.${a.type}`)}{a.requires_consent ? ` · ${t("market.you_confirm")}` : ""}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
